import { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Icon, SectionLabel } from "../kit";
import type { BandLayoutPose, BandLayoutPreset, DiamondConfig } from "../../shared/api-contract";
import type { RecipeLookId } from "./recipes/looks";

/**
 * "Add or remove rings" — the ring-set manager (contract v43).
 *
 * The reference's modal, control for control: a **Current compilation** list with
 * one row per ring (rename, hide, move ←/→, delete — the move buttons disabled at
 * the ends) and an **Add a ring** section offering its three product types, where
 * Memoire pushes a second screen to pick one of three eternity presets.
 *
 * Every action is a callback: the RPC work, the busy flag and the undo history
 * all belong to `RingBuilder`, which owns the set. This file also holds the set
 * MODEL (`RingRow`, the label table, the context the footer reads) because the
 * manager, the ring switcher, the spec sheet and the save document all have to
 * agree on what a ring is called.
 */

export type RingType = "wedding" | "memoire" | "engagement";

export interface RingRow {
  /** User-typed name. Absent ⇒ the positional label. */
  name?: string;
  type: RingType;
  /** "Hide ring" — host-side, mirrored to the engine by `setRingVisible`. */
  visible: boolean;
}

/** The first two rings keep the names the pair always had; past that they are
 * numbered. One table, three consumers (switcher · manager · spec sheet). */
export const RING_LABELS = ["Her Ring", "His Ring"];

export function ringLabel(rows: RingRow[], i: number): string {
  return rows[i]?.name?.trim() || RING_LABELS[i] || `Ring ${i + 1}`;
}

/**
 * Where index `a` ends up after the ring at `from` is lifted out and dropped at
 * `to` — i.e. `splice(from,1)` then `splice(to,0,…)`. The moved ring lands on
 * `to`; everything strictly between the two shifts one step the other way.
 * Verified by brute force against a real splice for every (a, from, to) up to 8.
 */
export function indexAfterMove(a: number, from: number, to: number): number {
  if (a === from) return to;
  if (from < a && a <= to) return a - 1;
  if (to <= a && a < from) return a + 1;
  return a;
}

const RING_TYPE_LABELS: Record<RingType, string> = {
  wedding: "Wedding ring",
  memoire: "Memoire",
  engagement: "Engagement ring",
};

/**
 * The three Memoire presets behind the reference's "Choose a memoire" screen.
 *
 * A memoire IS an eternity band, so each preset is a full-circumference stone run
 * — `spreadDeg: 360` with `count: 0`, the engine's "fill the ring" path — and the
 * three differ by how the stones are held: shared-metal (our "Memoir version"
 * setting), channel-set, and rail/clamp-set. Applied through `setDiamond` rather
 * than as an `addRing` seed on purpose: the channel and clamp settings mill their
 * own groove there, and the configurator's constructor does not run that pass.
 */
export const MEMOIRE_VARIANTS: { id: string; label: string; note: string; diamond: Partial<DiamondConfig> }[] = [
  {
    id: "eternity-1",
    label: "Eternity I",
    note: "Shared metal, stones set right around",
    diamond: { style: "memoir", count: 0, spreadDeg: 360, spacing: "half-stone" },
  },
  {
    id: "eternity-2",
    label: "Eternity II",
    note: "Channel set, touching all the way round",
    diamond: { style: "channel", count: 0, spreadDeg: 360, spacing: "touching" },
  },
  {
    id: "eternity-3",
    label: "Eternity III",
    note: "Rail set between two clamped walls",
    diamond: { style: "clamp", count: 0, spreadDeg: 360, spacing: "full-stone" },
  },
];

// ─── The set, shared with the footer ────────────────────────────────────────

export interface RingSet {
  rows: RingRow[];
  active: number;
  layout: BandLayoutPreset;
  linked: boolean;
  /** v68: a recipe's photographed pose, look and partner size (the share link carries them). */
  pose?: BandLayoutPose | null;
  look?: RecipeLookId;
  recipePartnerSizeUS?: number;
}

/** Default = the lone ring every builder boots with, so a consumer mounted
 * outside `RingBuilder` still reads something true. */
const RingSetCtx = createContext<RingSet>({
  rows: [{ type: "wedding", visible: true }],
  active: 0,
  layout: "side-by-side",
  linked: false,
});

export const RingSetProvider = RingSetCtx.Provider;

/** The live ring set: rows (name/type/visibility), the active index, the
 * arrangement and the link state. Read by the footer's save + per-ring export. */
export function useRingSet(): RingSet {
  return useContext(RingSetCtx);
}

// ─── The modal ──────────────────────────────────────────────────────────────

export interface RingManagerModalProps {
  rows: RingRow[];
  active: number;
  busy: boolean;
  /** Room for another ring (the viewer's own cap, not a product rule). */
  canAdd: boolean;
  /** `setRingVisible` / `moveRing` exist on this viewer (caps `ringManagerV43`).
   * RPC has no timeout, so Hide and Move stay disabled without them. */
  canManage: boolean;
  /** Whether THIS row's ring can actually be removed — an older viewer no-ops
   * `removeRing(0)` and only knows how to drop ring 1, and a Delete that removes
   * a different ring than the row it sits on is worse than a disabled one. */
  canDelete: (index: number) => boolean;
  onClose: () => void;
  onSelect: (index: number) => void;
  onRename: (index: number, name: string) => void;
  onVisible: (index: number, visible: boolean) => void;
  onMove: (from: number, to: number) => void;
  onDelete: (index: number) => void;
  onAdd: (type: RingType, memoire?: Partial<DiamondConfig>) => void;
}

export function RingManagerModal({
  rows, active, busy, canAdd, canManage, canDelete,
  onClose, onSelect, onRename, onVisible, onMove, onDelete, onAdd,
}: RingManagerModalProps) {
  /** null = the type list, "memoire" = the "Choose a memoire" screen. */
  const [picking, setPicking] = useState<"memoire" | null>(null);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add or remove rings"
      onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-on-surface/40 p-6 backdrop-blur-sm"
    >
      <div
        data-testid="ring-manager"
        className="my-auto w-full max-w-[520px] rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-7 shadow-[0_24px_80px_rgba(0,0,0,0.18)]"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-display text-headline-md text-primary">Add or remove rings</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-variant/50"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <SectionLabel>Current compilation</SectionLabel>
          {rows.map((row, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                i === active ? "border-primary/60 bg-surface" : "border-outline-variant/40 bg-surface-container-low"
              }`}
            >
              <button
                type="button"
                title="Select"
                aria-label={`Edit ${ringLabel(rows, i)}`}
                onClick={() => onSelect(i)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:text-primary"
              >
                <Icon name={i === active ? "radio_button_checked" : "radio_button_unchecked"} size={16} />
              </button>

              <div className="flex min-w-0 flex-1 flex-col">
                {/* The reference's `title=Rename` name button. Straight to an
                    input: a click-to-reveal edit state is one interaction more
                    for exactly the same result. */}
                <input
                  type="text"
                  title="Rename"
                  aria-label={`Name of ${ringLabel(rows, i)}`}
                  value={row.name ?? ""}
                  placeholder={ringLabel(rows, i)}
                  maxLength={40}
                  onChange={(e) => onRename(i, e.target.value)}
                  className="w-full truncate rounded-md border border-transparent bg-transparent px-1 py-0.5 font-display text-[12px] font-semibold uppercase tracking-[0.08em] text-primary outline-none placeholder:text-on-surface-variant/70 hover:border-outline-variant/50 focus:border-secondary"
                />
                <span className="px-1 font-body text-[10px] text-outline">{RING_TYPE_LABELS[row.type]}</span>
              </div>

              <RowAction
                icon={row.visible ? "visibility" : "visibility_off"}
                title={row.visible ? "Hide ring" : "Show ring"}
                disabled={busy || !canManage}
                onClick={() => onVisible(i, !row.visible)}
              />
              <RowAction
                icon="arrow_back"
                title="Move left"
                disabled={busy || !canManage || i === 0}
                onClick={() => onMove(i, i - 1)}
              />
              <RowAction
                icon="arrow_forward"
                title="Move right"
                disabled={busy || !canManage || i === rows.length - 1}
                onClick={() => onMove(i, i + 1)}
              />
              <RowAction
                icon="delete"
                title="Delete"
                disabled={busy || !canDelete(i)}
                onClick={() => onDelete(i)}
              />
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-2">
          {picking === "memoire" ? (
            <>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPicking(null)}
                  className="font-body text-[11px] text-on-surface-variant transition-colors hover:text-primary"
                >
                  ‹ Back
                </button>
                <SectionLabel>Choose a memoire</SectionLabel>
              </div>
              {MEMOIRE_VARIANTS.map((v) => (
                <AddRow
                  key={v.id}
                  label={v.label}
                  note={v.note}
                  disabled={busy || !canAdd}
                  onClick={() => { setPicking(null); onAdd("memoire", v.diamond); }}
                />
              ))}
            </>
          ) : (
            <>
              <SectionLabel>Add a ring</SectionLabel>
              <AddRow
                label={RING_TYPE_LABELS.wedding}
                note="A plain band, ready to shape"
                disabled={busy || !canAdd}
                onClick={() => onAdd("wedding")}
              />
              <AddRow
                label={RING_TYPE_LABELS.memoire}
                note="An eternity band — three presets"
                disabled={busy || !canAdd}
                onClick={() => setPicking("memoire")}
              />
              <AddRow
                label={RING_TYPE_LABELS.engagement}
                note="A band with the head assembly attached"
                disabled={busy || !canAdd}
                onClick={() => onAdd("engagement")}
              />
              {!canAdd ? (
                <span className="font-body text-[10px] text-outline">
                  That is as many rings as the viewer will pose at once.
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function RowAction({
  icon, title, disabled, onClick,
}: { icon: string; title: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-outline-variant/50 text-on-surface-variant transition-colors hover:border-primary hover:text-primary disabled:opacity-25 disabled:hover:border-outline-variant/50 disabled:hover:text-on-surface-variant"
    >
      <Icon name={icon} size={15} />
    </button>
  );
}

function AddRow({
  label, note, disabled, onClick,
}: { label: string; note: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3 py-2.5 text-left transition-colors hover:border-primary disabled:opacity-40 disabled:hover:border-outline-variant/40"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-outline-variant/50 text-on-surface-variant">
        <Icon name="add" size={15} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="font-display text-[12px] font-semibold uppercase tracking-[0.08em] text-primary">{label}</span>
        <span className="font-body text-[10px] text-outline">{note}</span>
      </span>
    </button>
  );
}
